import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getPages, updateDynamicPageHtml } from '../../services/pageService';

const PageList = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await getPages();
      if (data && Array.isArray(data.pages)) {
        setPages(data.pages);
      } else {
        setPages([]);
        console.warn('Expected array of pages but got:', data);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Sayfalar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPage = () => {
    navigate('/pages/add');
  };

  const handleEditPage = (page) => {
    navigate(`/pages/edit/${page.guid}`);
  };

  const handleConfirmDelete = (page) => {
    setSelectedPage(page);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedPage) return;
    
    try {
      setLoading(true);
      // Dinamik sayfayı silmek için HTML içeriğini boşaltıyoruz
      const response = await updateDynamicPageHtml(selectedPage.guid, '');
      
      if (response?.isSuccess) {
        await fetchPages();
        setShowDeleteModal(false);
      } else {
        setError('Sayfa silinirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Sayfa silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Container fluid className="main-content-container px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">Dinamik Sayfalar</h1>
        <Button variant="primary" onClick={handleAddPage}>
          Yeni Dinamik Sayfa Ekle
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && pages.length === 0 ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Yükleniyor...</span>
          </Spinner>
        </div>
      ) : pages.length === 0 ? (
        <Alert variant="info">Henüz hiç dinamik sayfa eklenmemiş.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Başlık</th>
              <th>Sayfa URL'i</th>
              <th>Oluşturulma Tarihi</th>
              <th>Durum</th>
              <th style={{ width: '180px' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <tr key={page.guid}>
                <td>{page.title}</td>
                <td>{page.url}</td>
                <td>{formatDate(page.createdDate)}</td>
                <td>
                  {page.isActive ? (
                    <span className="badge bg-success">Aktif</span>
                  ) : (
                    <span className="badge bg-secondary">Pasif</span>
                  )}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEditPage(page)}
                  >
                    Düzenle
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleConfirmDelete(page)}
                  >
                    Sil
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Sayfa Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPage && (
            <p>
              <strong>{selectedPage.title}</strong> başlıklı sayfayı silmek istediğinizden emin misiniz?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                İşleniyor...
              </>
            ) : (
              'Sil'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PageList;
